import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader, Loading, useFetch } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const FIELDS = [
  ["perso_km_threshold", "Km personnels anormalement élevés", "km par week-end", "Module commerciaux — alerte si le km perso d'un week-end dépasse ce seuil."],
  ["incoherent_km_max", "Valeur incohérente au-delà de", "km par week-end", "Module commerciaux — un écart négatif ou supérieur à cette valeur est signalé comme incohérent."],
  ["check_missing_days", "Contrôle manquant après", "jour(s) sans contrôle", "Module livraison — un véhicule actif sans contrôle depuis ce délai est signalé."],
  ["high_consumption_l100", "Consommation élevée", "L/100 km", "Réservé à l'analyse carburant (indicatif)."],
  ["maintenance_reminder_days", "Rappel d'entretien (date)", "jours avant l'échéance", "Entretien — un entretien passe « À prévoir » ce nombre de jours avant sa date d'échéance."],
  ["maintenance_reminder_km", "Rappel d'entretien (kilométrage)", "km avant l'échéance", "Entretien — un entretien passe « À prévoir » lorsqu'il reste moins de ce kilométrage."],
  ["doc_alert_days_1", "Alerte documents — 1er rappel", "jours avant", "Assurances, documents véhicules et permis — première alerte avant l'échéance."],
  ["doc_alert_days_2", "Alerte documents — 2e rappel", "jours avant", "Deuxième alerte avant l'échéance."],
  ["doc_alert_days_3", "Alerte documents — dernier rappel", "jours avant", "Dernière alerte avant l'échéance (ensuite : Expiré)."],
  ["ocr_max_gap_km", "Écart anormal (relevé par photo)", "km depuis le dernier", "Relevé commercial — au-delà de cet écart avec le dernier kilométrage connu, une validation manuelle est demandée."],
];

export default function Thresholds() {
  const { data, loading } = useFetch("/settings/alerts");
  const [f, setF] = useState(null);
  useEffect(() => {
    if (data) setF(data);
  }, [data]);
  if (loading || !f) return <Loading />;
  const save = async () => {
    try {
      const body = Object.fromEntries(FIELDS.map(([k]) => [k, Number(f[k])]));
      const { data: d } = await api.put("/settings/alerts", body);
      setF(d);
      toast.success("Seuils enregistrés");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="max-w-2xl" data-testid="thresholds-page">
      <PageHeader overline="Paramètres" title="Seuils d'alerte" />
      <div className="bg-white border border-slate-200 rounded-xl divide-y rise">
        {FIELDS.map(([k, label, unit, hint]) => (
          <div key={k} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div><div className="font-semibold text-[#0E2D43]">{label}</div><div className="text-xs text-slate-500 mt-0.5">{hint}</div></div>
            <div className="flex items-center gap-2 shrink-0">
              <Input type="number" step="any" min={1} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="w-28 h-11 font-mono text-right" data-testid={`threshold-${k}`} />
              <span className="text-xs text-slate-500 w-28">{unit}</span>
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} className="mt-5 h-11 px-6 rounded-md bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold transition-colors" data-testid="thresholds-save-button">Enregistrer</button>
      <NotifyRecipients />
    </div>
  );
}

function NotifyRecipients() {
  const { data } = useFetch("/settings/notify");
  const [txt, setTxt] = useState(null);
  useEffect(() => {
    if (data) setTxt(data.accident_emails.join("\n"));
  }, [data]);
  if (txt === null) return null;
  const save = async () => {
    try {
      const accident_emails = txt.split(/[\s,;]+/).filter(Boolean);
      const bad = accident_emails.find((m) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(m));
      if (bad) {
        toast.error(`Adresse e-mail invalide : ${bad}`);
        return;
      }
      const { data: d } = await api.put("/settings/notify", { accident_emails });
      setTxt(d.accident_emails.join("\n"));
      toast.success("Destinataires enregistrés");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-xl p-5" data-testid="notify-recipients">
      <div className="font-semibold text-[#0E2D43]">Destinataires des alertes sinistre</div>
      <div className="text-xs text-slate-500 mt-0.5 mb-3">Une adresse par ligne. Un e-mail part automatiquement à chacune dès qu'un accident / sinistre est enregistré.</div>
      <Textarea rows={3} value={txt} onChange={(e) => setTxt(e.target.value)} className="font-mono text-sm" data-testid="notify-recipients-input" />
      <button onClick={save} className="mt-3 h-10 px-5 rounded-md bg-[#0E2D43] text-white font-bold" data-testid="notify-recipients-save">Enregistrer les destinataires</button>
    </div>
  );
}
